import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, TouchableOpacity, ActivityIndicator, SafeAreaView, StyleSheet } from 'react-native';
import * as Location from 'expo-location';
import { get } from '../api/client';
import CompetitionCard from '../components/CompetitionCard';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { BLUE, BG, TEXT, TEXT_MUTED, CARD } from '../theme/colors';

type Competition = { id: number; name: string; date: string; location?: string; status?: string; latitude?: number | null; longitude?: number | null };
type UserLocation = { latitude: number; longitude: number };
type Props = NativeStackScreenProps<any, 'Competitions'>;

function todayLabel(): string {
  const d = new Date();
  const months = ['januar','februar','mars','april','mai','juni','juli','august','september','oktober','november','desember'];
  return `${d.getDate()}. ${months[d.getMonth()]}`;
}



export default function CompetitionsScreen({ navigation }: Props) {
  const [items, setItems] = useState<Competition[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [ticketsMap, setTicketsMap] = useState<Record<number, any[]>>({});

  useEffect(() => {
    setLoading(true);
    get<Competition[]>('/competitions')
      .then(setItems)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!items) return;
    const toFetch = items.filter(i => i.status !== 'completed');
    Promise.all(
      toFetch.map(c =>
        get<any>(`/competitions/${c.id}/kiosk`)
          .then(res => ({ id: c.id, res }))
          .catch(() => null)
      )
    ).then(results => {
      const map: Record<number, any[]> = {};
      results.forEach(r => {
        if (!r || !r.res?.is_open) return;
        const tickets: any[] = [];
        (r.res.kiosks ?? []).forEach((k: any) => {
          Object.entries(k.categories ?? {}).forEach(([cat, items]: [string, any]) => {
            if (cat.toLowerCase() === 'billetter') tickets.push(...(items as any[]));
          });
        });
        if (tickets.length > 0) map[r.id] = tickets;
      });
      setTicketsMap(map);
    });
  }, [items]);

  useEffect(() => {
    (async () => {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') return;
      const loc = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
      setUserLocation({ latitude: loc.coords.latitude, longitude: loc.coords.longitude });
    })();
  }, []);

  if (loading) return <View style={s.center}><ActivityIndicator color={BLUE} /></View>;
  if (error) return <View style={s.center}><Text style={{ color: '#c00', padding: 16 }}>Feil: {error}</Text></View>;

  const active   = (items || []).filter(i => i.status !== 'completed' && i.status !== 'upcoming');
  const upcoming = (items || []).filter(i => i.status === 'upcoming');
  const past     = (items || []).filter(i => i.status === 'completed');

  type Row =
    | { _t: 'header' }
    | { _t: 'upcomingHeader' }
    | { _t: 'pastHeader' }
    | { _t: 'item'; data: Competition };

  const rows: Row[] = [
    { _t: 'header' },
    ...active.map(c => ({ _t: 'item' as const, data: c })),
    ...(upcoming.length > 0 ? [{ _t: 'upcomingHeader' as const }] : []),
    ...upcoming.map(c => ({ _t: 'item' as const, data: c })),
    ...(past.length > 0 ? [{ _t: 'pastHeader' as const }] : []),
    ...past.map(c => ({ _t: 'item' as const, data: c })),
  ];

  return (
    <SafeAreaView style={s.bg}>
      <FlatList
        data={rows}
        keyExtractor={(r, idx) => r._t === 'item' ? String(r.data.id) : `${r._t}-${idx}`}
        renderItem={({ item: r }) => {
          if (r._t === 'header') return (
            <View style={s.header}>
              <Text style={s.dateLabel}>I dag · {todayLabel()}</Text>
              <Text style={s.title}>Konkurranser</Text>
            </View>
          );
          if (r._t === 'upcomingHeader') return (
            <View style={s.sectionLabel}><Text style={s.sectionText}>Kommende konkurranser</Text></View>
          );
          if (r._t === 'pastHeader') return (
            <View style={s.sectionLabel}><Text style={s.sectionText}>Tidligere konkurranser</Text></View>
          );
          if (r._t === 'item') return (
            <TouchableOpacity onPress={() => navigation.navigate('CompetitionTabs', { competitionId: r.data.id, competitionName: r.data.name, competitionDate: r.data.date, competitionStatus: r.data.status })}>
              <CompetitionCard competition={r.data} userLocation={userLocation} tickets={ticketsMap[r.data.id]} />
            </TouchableOpacity>
          );
          return null;
        }}
        ListEmptyComponent={<View style={s.center}><Text style={{ color: '#888' }}>Ingen konkurranser tilgjengelig.</Text></View>}
        ListFooterComponent={() => (
          <View style={s.footer}><Text style={s.footerText}>Powered by Smart Gymnastics</Text></View>
        )}
      />
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  bg:          { flex: 1, backgroundColor: BG },
  center:      { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header:      { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 14 },
  dateLabel:   { fontSize: 13, color: '#777', marginBottom: 2 },
  title:       { fontSize: 26, fontWeight: '700', color: TEXT },
  sectionLabel:{ paddingHorizontal: 16, paddingTop: 20, paddingBottom: 6 },
  sectionText: { fontSize: 12, fontWeight: '700', color: '#888', letterSpacing: 0.5 },
  footer:      { padding: 24, alignItems: 'center' },
  footerText:  { color: '#bbb', fontSize: 13 },
});
